<template>
  <span
    :is="is"

    :class="classes"
    :disabled="disabled || loading"

    v-bind="bind"

    @click="click"
    @blur="blur"
    @focus="focus"
  >
    <font-awesome-icon
      v-if="icon && iconPosition === 'left'"
      :icon="icon"
      class="cordel-button-icon"
    />

    <context-loading
      v-if="loading"
      class="cordel-button-loading"
      light
    />

    <span
      v-if="$slots.default"
      class="cordel-button-label"
    >
      <slot />
    </span>

    <font-awesome-icon
      v-if="icon && iconPosition === 'right'"
      :icon="icon"
      class="cordel-button-icon"
    />
  </span>
</template>

<script>
  import ContextLoading from '@cordel/components/Context/Loading.vue';

  const COLORS = ['primary', 'secondary', 'highlight', 'danger'];
  const SIZES = ['large', 'medium', 'small'];
  const ICON_POSITION = ['left', 'right'];
  const ICON_SIZE = ['medium', 'large'];

  export default {
    name: 'CordelButton',
    components: {
      ContextLoading,
    },
    props: {
      disabled: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: [String, Array],
        default: '',
      },
      iconPosition: {
        type: String,
        default: ICON_POSITION[0],
        validator(value) {
          return ICON_POSITION.includes(value);
        },
      },
      iconSize: {
        type: String,
        default: ICON_SIZE[0],
        validator(value) {
          return ICON_SIZE.includes(value);
        },
      },
      color: {
        type: String,
        default: COLORS[0],
        validator(value) {
          return COLORS.includes(value);
        },
      },
      size: {
        type: String,
        default: SIZES[1],
        validator(value) {
          return SIZES.includes(value);
        },
      },
      href: {
        type: [String, Object],
        default: null,
      },
    },
    computed: {
      classes() {
        const classes = [
          'cordel-button',
          `color--${this.color}`,
          `size--${this.size}`,
        ];

        if (this.loading) {
          classes.push('state--loading');
        }

        if (this.disabled) {
          classes.push('state--disabled');
        }

        if (this.icon) {
          classes.push(`variant--icon-size--${this.iconSize}`);

          if (this.iconed) {
            classes.push('variant--iconed');
          } else {
            classes.push(`variant--icon-position--${this.iconPosition}`);
          }
        }

        return classes;
      },
      iconed() {
        return !this.$slots.default;
      },
      is() {
        if (this.href) {
          if (this.disabled || this.loading) {
            return 'span';
          }

          if (typeof this.href === 'string') {
            return 'a';
          }

          return 'router-link';
        }

        return 'button';
      },
      bind() {
        if (this.href) {
          if (typeof this.href === 'string') {
            return {
              href: this.href,
            };
          }

          return {
            to: this.href,
          };
        }

        return {};
      },
    },
    methods: {
      click(e) {
        this.$emit('click', e);
      },
      blur(e) {
        this.$emit('blur', e);
      },
      focus(e) {
        this.$emit('focus', e);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/mixins/button.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-button {
    @include cordel-input-reset;
    @include cordel-button;
  }

  .size--small {
    @include button-size(
      var(--cordel-legacy-font-size-s),
      var(--cordel-legacy-font-size-s),
      var(--cordel-legacy-spacing-micro) var(--cordel-legacy-spacing-xxxs),
      var(--cordel-legacy-spacing-nano),
    );
  }

  .size--medium {
    @include button-size(
      var(--cordel-legacy-font-size-m),
      var(--cordel-legacy-font-size-s),
      var(--cordel-legacy-spacing-xxxs) var(--cordel-legacy-spacing-l),
      var(--cordel-legacy-spacing-xxxs),
    );
  }

  .size--large {
    @include button-size(
      var(--cordel-legacy-font-size-l),
      var(--cordel-legacy-font-size-m),
      var(--cordel-legacy-spacing-xs) var(--cordel-legacy-spacing-giga),
      var(--cordel-legacy-spacing-xxxs),
    );
  }

  .color--primary {
    @include button-color(
      var(--cordel-legacy-color-neutral-darker),
      var(--cordel-legacy-color-neutral-heavy),
      var(--cordel-legacy-color-constant-light),
    );
  }

  .color--secondary {
    @include button-color(
      var(--cordel-legacy-color-neutral-dark),
      var(--cordel-legacy-color-constant-dark),
      var(--cordel-legacy-color-constant-light),
    );
  }

  .color--highlight {
    @include button-color(
      var(--cordel-legacy-color-highlight-primary),
      var(--cordel-legacy-color-highlight-dark),
      var(--cordel-legacy-color-constant-dark),
    );
  }

  .color--danger {
    @include button-color(
      var(--cordel-legacy-color-alert-danger),
      var(--cordel-legacy-color-alert-danger-dark),
      var(--cordel-legacy-color-constant-light),
    );
  }

  .cordel-button-label {
    color: inherit;
  }

  .cordel-button-icon {
    font-size: 1em;

    .variant--icon-size--large & {
      font-size: 1.25em;
    }
  }

  .cordel-button-loading {
    bottom: 0;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    top: 0;
  }
</style>
